import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { timesheetEndpoints } from '@/lib/api/endpoints';
import { timesheetKeys } from '@/lib/queryKeys';
import type { CreateTimesheetEntryDto, TimesheetDayEntry } from '@/types/timesheet';

/**
 * Office-create a time entry with explicit start/end times. `appointmentId` is
 * optional — pass one to attach the entry to a job, or `null` for OVERHEAD
 * (non-job) time. Goes through `POST /time-entries` (appointment in the body),
 * which handles both cases; the appointment-scoped route can't express overhead.
 */
export function useCreateTimesheetEntry() {
  const queryClient = useQueryClient();
  return useApiMutation<
    TimesheetDayEntry,
    { appointmentId: string | null; dto: CreateTimesheetEntryDto }
  >({
    mutationFn: ({ appointmentId, dto }) =>
      apiPost<TimesheetDayEntry, CreateTimesheetEntryDto>(
        operationsClient,
        timesheetEndpoints.createSelected(),
        { ...dto, appointmentId: appointmentId ?? null },
      ),
    onSuccess: (_entry, { appointmentId }) => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all() });
      toast.success(appointmentId ? 'Time entry added' : 'Overhead time added');
    },
  });
}
